import React, { useEffect, useState } from 'react';
import { Authenticator, Button, CheckboxField, useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import { emailMarketing } from '../../services/emailMarketingApi';
import awsExports from '../../aws-exports';
import './signupEmail.css';

export const defaultAuthHero = {
  title: 'Join the', highlight: 'movement.',
  description: 'Real Dancehall. Real Jamaican culture. Your first move starts here.',
  benefits: ['Start with free lessons', 'Learn from Jamaican dancers', 'Practice anywhere, anytime'],
  imageUrl: '', signUpTitle: 'Create your free account.', signUpSubtitle: 'No credit card required.',
  signInTitle: 'Welcome back.', signInSubtitle: 'Your next move is waiting.',
  optInText: 'Email me Dancehall tips, free lessons, and JA Legends updates. I can unsubscribe anytime.',
  socialProofText: 'Join 50,000+ dancers worldwide',
};

const loginChoiceKey = 'jalegends:login-email-opt-in';
const rememberLoginChoice = (selected) => {
  try { sessionStorage.setItem(loginChoiceKey, selected ? 'yes' : 'no'); } catch (_) { /* No persistent choice. */ }
};
const readLoginChoice = () => {
  try { return sessionStorage.getItem(loginChoiceKey) === 'yes'; } catch (_) { return false; }
};
const clearLoginChoice = () => {
  try { sessionStorage.removeItem(loginChoiceKey); } catch (_) { /* Nothing to clean up. */ }
};

function useAuthHero() {
  const [hero, setHero] = useState(defaultAuthHero);
  useEffect(() => {
    const endpoint = awsExports.aws_cloud_logic_custom?.find((item) => item.name === 'apiemailmarketing')?.endpoint;
    if (!endpoint) return undefined;
    const controller = new AbortController();
    fetch(`${endpoint}/emailMarketing?op=authHero`, { signal: controller.signal })
      .then((response) => { if (!response.ok) throw new Error('Hero unavailable'); return response.json(); })
      .then((result) => { if (result?.hero) setHero({ ...defaultAuthHero, ...result.hero }); })
      .catch(() => { /* Keep the polished local default when the public API is unavailable. */ });
    return () => controller.abort();
  }, []);
  return hero;
}

function AuthHero() {
  const hero = useAuthHero();
  const image = hero.imageUrl || '/auth-dance-hero.png';
  return <div className="auth-experience-hero" style={{ backgroundImage: `linear-gradient(90deg, rgba(8, 7, 6, .9), rgba(8, 7, 6, .42) 62%, rgba(8, 7, 6, .05)), url("${image}")` }}>
    <div className="auth-experience-brand"><span>JA</span><div>LEGENDS<small>D A N C E</small></div></div>
    <div className="auth-experience-copy">
      <p className="auth-experience-kicker">YOUR DANCEHALL JOURNEY STARTS HERE</p>
      <h1>{hero.title}<br /><em>{hero.highlight}</em></h1>
      <p className="auth-experience-description">{hero.description}</p>
      <ul>{(hero.benefits || defaultAuthHero.benefits).map((benefit, index) => <li key={index}><span aria-hidden="true">{['✦', '★', '♫'][index]}</span>{benefit}</li>)}</ul>
    </div>
  </div>;
}

function AuthCardHeading({ mode }) {
  const hero = useAuthHero();
  return <div className="auth-experience-card-heading"><h2>{mode === 'signup' ? hero.signUpTitle : hero.signInTitle}</h2><p>{mode === 'signup' ? hero.signUpSubtitle : hero.signInSubtitle}</p></div>;
}

export function MarketingOptInField({ name, selected, onChange }) {
  const hero = useAuthHero();
  return <CheckboxField name={name} value="yes" checked={selected} onChange={onChange} label={hero.optInText} />;
}

export function SignupSocialProof() {
  const hero = useAuthHero();
  const count = /^(Join\s+)([\d,.]+\+?)(.*)$/i.exec(hero.socialProofText);
  return <div className="auth-experience-social-proof" aria-label={hero.socialProofText}>
    <div className="auth-experience-social-icons" aria-hidden="true"><span>♫</span><span>★</span><span>✦</span></div>
    <span className="auth-experience-social-text">{count ? <>{count[1]}<strong>{count[2]}</strong>{count[3]}</> : hero.socialProofText}</span>
    <svg className="auth-experience-social-globe" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" /></svg>
  </div>;
}

const choiceKey = (username) => `jalegends:signup-email:${String(username || '').trim().toLowerCase()}`;

const rememberChoice = (username, selected) => {
  try { sessionStorage.setItem(choiceKey(username), selected ? 'yes' : 'no'); } catch (_) { /* Confirmation falls back to no marketing. */ }
};

const readChoice = (username) => {
  try { return sessionStorage.getItem(choiceKey(username)) === 'yes'; } catch (_) { return false; }
};

const forgetChoice = (username) => {
  try { sessionStorage.removeItem(choiceKey(username)); } catch (_) { /* Nothing to clean up. */ }
};

let pendingSync;

// Cognito confirmation and the first authenticated page are separate screens.
// Keep an explicit yes until the preference API confirms that it was saved.
export const syncPendingSignupConsent = (user) => {
  const username = user?.attributes?.email || user?.username;
  if (!username || !readChoice(username)) return Promise.resolve(false);
  if (pendingSync) return pendingSync;
  pendingSync = emailMarketing('setPreference', { consent: true })
    .then((result) => {
      if (!result?.consent) throw new Error('Marketing consent was not saved');
      forgetChoice(username);
      window.dispatchEvent(new CustomEvent('marketing-preference-changed', { detail: { consent: true } }));
      return true;
    })
    .finally(() => { pendingSync = undefined; });
  return pendingSync;
};

export const signupEmailOptions = {
  components: {
    Header: AuthHero,
    SignIn: {
      Header() { return <AuthCardHeading mode="signin" />; },
      Footer() {
        const { toResetPassword } = useAuthenticator((context) => [context.toResetPassword]);
        const [selected, setSelected] = useState(false);
        useEffect(() => { rememberLoginChoice(false); return clearLoginChoice; }, []);
        return <div className="auth-experience-signin-footer">
          <div className="signup-dance-opt-in"><MarketingOptInField name="login_marketing_opt_in" selected={selected} onChange={(event) => { setSelected(event.target.checked); rememberLoginChoice(event.target.checked); }} /></div>
          <Button size="small" variation="link" onClick={toResetPassword}>Forgot your password?</Button>
          <SignupSocialProof />
        </div>;
      },
    },
    SignUp: {
      Header() {
        return <AuthCardHeading mode="signup" />;
      },
      Footer() {
        return <SignupSocialProof />;
      },
      FormFields() {
        return <>
          <Authenticator.SignUp.FormFields />
          <div className="signup-dance-opt-in">
            <MarketingOptInField name="marketing_opt_in" />
          </div>
        </>;
      },
    },
  },
  services: {
    async handleSignIn({ username, password }) {
      const result = await Auth.signIn(username, password);
      if (readLoginChoice()) rememberChoice(username, true);
      clearLoginChoice();
      return result;
    },
    async validateCustomSignUp(formData) {
      rememberChoice(formData.username || formData.email, formData.marketing_opt_in === 'yes');
    },
    async handleSignUp(input) {
      const { username, password, attributes = {} } = input;
      const { marketing_opt_in: choice, ...cognitoAttributes } = attributes;
      if (choice !== undefined) rememberChoice(username, choice === 'yes');
      return Auth.signUp({ username, password, attributes: cognitoAttributes, autoSignIn: { enabled: true } });
    },
    async handleConfirmSignUp({ username, code }) {
      const result = await Auth.confirmSignUp(username, code, {
        clientMetadata: { marketing_opt_in: readChoice(username) ? 'yes' : 'no' },
      });
      if (!readChoice(username)) forgetChoice(username);
      return result;
    },
  },
};
